import React from 'react'
import { Alert, Button, Card, Col, Row, Space, Tag, Typography, message } from 'antd'
import {
    AudioOutlined,
    CheckCircleOutlined,
    CloudDownloadOutlined,
    CloudUploadOutlined,
    CodeOutlined,
    DatabaseOutlined,
    FileExcelOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FolderOpenOutlined,
    PictureOutlined,
    SafetyOutlined,
    KeyOutlined,
} from '@ant-design/icons'

const { Text } = Typography

export const AIC_TOOLKIT_VERSION = '1.0.2'
export const AIC_TOOLKIT_UPDATED_AT = '2026-08-14'
export const AIC_TOOLKIT_DOWNLOAD_URL = `${process.env.PUBLIC_URL || ''}/downloads/aic-data-asset-upload-toolkit-v1.0.2.zip`

export const AIC_SESSION_PROMPT = `使用 $aic-data-ingestion，把我选择的文件接入 AIC 项目 <项目ID>（<项目名称>）。
这是该项目的首次接入或后续补充上传：同一个 AIC 项目请继续复用当前 Codex 会话；只有切换到其他 AIC 项目时才新建会话。一个会话始终只处理一个目标项目。
先调用 bootstrap 和 context 核验目标项目与权限，只做只读分析，给出异常清单、修正规则、字段映射、目标表和脱敏预览，不要上传或写库。
得到我确认后再处理数据；发布前再次向我确认影响范围，最后 commit 并报告 publishedVersion、服务端行数、附件数量和来源 SHA-256。`

export const AIC_PASSWORD_SETUP_PROMPT = `请在当前电脑上安全配置 AIC 数据库密码环境变量，直接执行，不要让我粘贴任何密钥或密码。

1. 检查当前进程环境变量 LECHUN_API_KEY 是否存在，只能判断存在与否，禁止输出、回显、记录或保存它。如果不存在，停止并提示我先在个人中心配置 API Key。
2. 使用当前环境变量中的 LECHUN_API_KEY，通过 HTTPS 发起 POST 请求：
   https://scmapi.lechun.cc/lechun-baseservice/vibeProject/bootstrap/aic-database-password
   请求头只能使用 X-API-Key，值从 LECHUN_API_KEY 环境变量读取。不要使用 curl -v、shell 调试模式或任何会打印请求头的方式。
3. 在内存中解析响应。只有 HTTP 200、JSON 顶层 status=200，且 value.password 是非空字符串时才继续；否则只报告接口返回的非敏感错误信息。禁止输出完整响应体或 password。
4. 自动判断操作系统并设置 LECHUN_AIC_DB_PASSWORD：
   - macOS：从内存变量调用 /bin/launchctl setenv LECHUN_AIC_DB_PASSWORD，命令文本只能引用变量，禁止把密码值展开到工具调用、日志或聊天中。
   - Windows：使用当前 PowerShell 进程中的内存变量调用 [Environment]::SetEnvironmentVariable('LECHUN_AIC_DB_PASSWORD', password, 'User')，禁止把密码拼进命令文本。
   - 其他系统：停止并只说明当前提示词暂不支持，不得展示密码。
5. 配置后立即清理保存响应和密码的临时变量；不得写入临时文件、项目文件、聊天、终端历史或日志。只检查环境变量是否已设置，禁止读取并展示其值。
6. 最后只回复“LECHUN_AIC_DB_PASSWORD 已配置，请完全退出并重新打开 Codex”或不含敏感信息的失败原因。`

const STEPS = [
    {
        icon: <CloudDownloadOutlined />,
        title: '下载并解压工具包',
        description: '下载 ZIP 后解压，得到“AIC项目数据资产上传”文件夹。不要删除其中的 AGENTS.md、.agents、scripts 和 .vibe。',
        result: '本地已经有完整工作台目录。',
    },
    {
        icon: <FolderOpenOutlined />,
        title: '在 Codex 中创建本地项目',
        description: '在 Codex 左侧选择解压后的文件夹创建项目，项目名称使用“AIC项目数据资产上传”。每个人只需要创建一个，后续可处理本人有权限的所有 AIC 项目。',
        result: 'Codex 已把该文件夹作为项目打开。',
    },
    {
        icon: <CodeOutlined />,
        title: '新建会话并选择数据',
        description: '同一个 AIC 项目复用同一个 Codex 会话，后续上传继续在该会话中选择文件并分析；切换到其他项目时才新建会话。一个会话只处理一个目标项目，先让 Codex 做只读分析。',
        result: '已确认异常、修正规则、字段映射和脱敏预览。',
    },
    {
        icon: <CloudUploadOutlined />,
        title: '确认后由 Codex 发布',
        description: '确认分析结果后，结构化数据由 Codex 写入 AIC 正式表，文档、图片、音频、PDF 等原件上传私有 OSS；发布前再次确认影响范围。',
        result: '以 commit 返回的版本、服务端行数、附件和来源哈希为准。',
    },
]

const FILE_TYPES = [
    { icon: <FileExcelOutlined />, title: '结构化数据', detail: 'Excel、CSV、表格及手工数据', color: '#389e0d' },
    { icon: <FileTextOutlined />, title: '文档', detail: 'Word、文本及其他业务文档', color: '#1677ff' },
    { icon: <PictureOutlined />, title: '图片', detail: '照片、截图、扫描件及设计稿', color: '#722ed1' },
    { icon: <AudioOutlined />, title: '音频', detail: '录音、访谈和其他声音文件', color: '#d46b08' },
    { icon: <FilePdfOutlined />, title: 'PDF', detail: '报告、合同及不可编辑资料', color: '#cf1322' },
]

function copyText(text, successMessage = '提示词已复制') {
    if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(text)
            .then(() => message.success(successMessage))
            .catch(() => message.warning('复制失败，请手动复制'))
        return
    }
    message.warning('当前浏览器不支持自动复制，请手动复制')
}

function AicCodexOnboardingTab({ onTabSwitch }) {
    return (
        <div className="profile-aic-onboarding">
            <section className="profile-aic-hero">
                <div className="profile-aic-hero-content">
                    <Tag color="cyan">AIC · All In Context</Tag>
                    <h1>在 Codex 中配置 AIC 项目数据资产上传</h1>
                    <p>
                        下载一次、长期复用。Codex 负责在本地识别和修正复杂业务数据，确认后再把结构化数据写入正式表，
                        并把文档、图片、音频等原件上传到私有 OSS。
                    </p>
                    <Space size={12} wrap>
                        <Button
                            type="primary"
                            size="large"
                            icon={<CloudDownloadOutlined />}
                            href={AIC_TOOLKIT_DOWNLOAD_URL}
                            download="AIC项目数据资产上传-v1.0.2.zip"
                        >
                            下载工具包
                        </Button>
                        <Text type="secondary">版本 {AIC_TOOLKIT_VERSION} · 更新于 {AIC_TOOLKIT_UPDATED_AT}</Text>
                    </Space>
                </div>
                <div className="profile-aic-hero-visual" aria-hidden="true">
                    <DatabaseOutlined />
                    <span>本地分析</span>
                    <b>→</b>
                    <SafetyOutlined />
                    <span>确认发布</span>
                </div>
            </section>

            <Alert
                type="info"
                showIcon
                className="profile-aic-prerequisite"
                message="开始前准备"
                description={(
                    <span>
                        操作系统需要配置 <Text code>LECHUN_API_KEY</Text>；写正式表时还需要
                        <Text code>LECHUN_AIC_DB_PASSWORD</Text>。凭据只能放在系统环境中，不能粘贴到会话或写入项目文件。
                        {onTabSwitch ? <Button type="link" size="small" onClick={() => onTabSwitch('apikey')}>前往 API Key 管理</Button> : null}
                    </span>
                )}
            />

            <Card bordered={false} className="profile-aic-prompt-card profile-aic-password-card">
                <div className="profile-aic-prompt-head">
                    <div>
                        <strong><KeyOutlined /> 一键配置 AIC 数据库密码</strong>
                        <div><Text type="secondary">复制给 Codex 执行；密码由当前用户的 API Key 安全获取，不会出现在提示词里。</Text></div>
                    </div>
                    <Button
                        type="primary"
                        icon={<CodeOutlined />}
                        onClick={() => copyText(AIC_PASSWORD_SETUP_PROMPT, 'AIC 密码配置提示词已复制')}
                    >
                        复制配置提示词
                    </Button>
                </div>
                <pre>{AIC_PASSWORD_SETUP_PROMPT}</pre>
            </Card>

            <section className="profile-aic-section">
                <div className="profile-aic-section-title">
                    <span>01</span>
                    <div>
                        <h2>四步完成配置和上传</h2>
                        <p>每个人只建一个工作台；同一项目持续复用一个会话，切换项目时再新建会话。</p>
                    </div>
                </div>
                <div className="profile-aic-step-grid">
                    {STEPS.map((step, index) => (
                        <Card key={step.title} bordered={false} className="profile-aic-step-card">
                            <div className="profile-aic-step-head">
                                <span className="profile-aic-step-number">{index + 1}</span>
                                <span className="profile-aic-step-icon">{step.icon}</span>
                            </div>
                            <h3>{step.title}</h3>
                            <p>{step.description}</p>
                            <div className="profile-aic-step-result">
                                <CheckCircleOutlined /> {step.result}
                            </div>
                        </Card>
                    ))}
                </div>
            </section>

            <section className="profile-aic-section">
                <div className="profile-aic-section-title">
                    <span>02</span>
                    <div>
                        <h2>新会话提示词</h2>
                        <p>替换项目 ID 和项目名称后发送；也可以直接用自然语言表达相同要求。</p>
                    </div>
                </div>
                <Card bordered={false} className="profile-aic-prompt-card">
                    <div className="profile-aic-prompt-head">
                        <strong>复制到已打开工作台的 Codex 会话</strong>
                        <Button icon={<CodeOutlined />} onClick={() => copyText(AIC_SESSION_PROMPT)}>复制提示词</Button>
                    </div>
                    <pre>{AIC_SESSION_PROMPT}</pre>
                </Card>
            </section>

            <section className="profile-aic-section">
                <div className="profile-aic-section-title">
                    <span>03</span>
                    <div>
                        <h2>支持的数据资产</h2>
                        <p>结构化数据和原始附件采用不同发布链路，但统一由 Codex 分析、确认和提交。</p>
                    </div>
                </div>
                <Row gutter={[16, 16]}>
                    {FILE_TYPES.map(item => (
                        <Col xs={24} sm={12} lg={8} xl={4} key={item.title} className="profile-aic-file-col">
                            <Card bordered={false} className="profile-aic-file-card">
                                <span style={{ color: item.color }}>{item.icon}</span>
                                <strong>{item.title}</strong>
                                <small>{item.detail}</small>
                            </Card>
                        </Col>
                    ))}
                </Row>
            </section>

            <section className="profile-aic-safety">
                <SafetyOutlined />
                <div>
                    <h2>两次确认，避免误写正式数据</h2>
                    <p>
                        第一次确认异常处理、字段映射和脱敏预览；第二次确认目标项目、导入模式、正式表和预计影响范围。
                        Codex 不允许自行建表、改表、授权、清空表或跨项目操作。
                    </p>
                </div>
            </section>
        </div>
    )
}

export default AicCodexOnboardingTab
